/**
 * Deals by date (D82) — read-only. Pick a date range (your local days), see totals per type and
 * asset, the deals themselves, and download everything as CSV (the export is audited).
 * Nothing on this page can change a deal.
 */
import type { DealFilter, DealTotal } from "@custos/api-client";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Notice,
  Segmented,
  Select,
  Skeleton,
  Spot,
  formatAmount,
  formatDateTime,
  toast,
} from "@custos/ui";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "../components/AdminShell";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { useConfig } from "../lib/queries";

type Type = NonNullable<DealFilter["type"]>;
type Status = NonNullable<DealFilter["status"]>;

const TYPE_LABEL: Record<DealTotal["type"], string> = {
  DEPOSIT: "Deposits",
  WITHDRAWAL: "Withdrawals",
  SWAP: "Swaps",
};

/** "2026-10-26" for <input type="date">, in local time. */
function dayInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local day start → ISO. The "to" day is included, so the range ends at the start of the next day. */
function range(fromDay: string, toDay: string): { from: string; to: string } | null {
  const from = new Date(`${fromDay}T00:00:00`);
  const to = new Date(`${toDay}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  to.setDate(to.getDate() + 1);
  return to > from ? { from: from.toISOString(), to: to.toISOString() } : null;
}

const usd = (cents: string | null) =>
  cents === null
    ? "—"
    : `$${(Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ReportsPage() {
  const config = useConfig();
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 29 * 86_400_000);
  const [fromDay, setFromDay] = useState(dayInput(monthAgo));
  const [toDay, setToDay] = useState(dayInput(today));
  const [type, setType] = useState<Type>("ALL");
  const [status, setStatus] = useState<Status>("COMPLETED");
  const [asset, setAsset] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const r = range(fromDay, toDay);
  const filter: DealFilter | null = r ? { ...r, type, status, ...(asset ? { asset } : {}) } : null;
  const report = useQuery({
    queryKey: ["deal-report", filter],
    queryFn: () => api.dealReport(filter!),
    enabled: filter !== null,
  });
  const symbols = [...new Set((config.data?.assets ?? []).map((a) => a.symbol))].sort();

  const download = async () => {
    if (!filter) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await api.dealCsv(filter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deals-${fromDay}-to-${toDay}.csv`;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast("CSV downloaded");
    } catch (e) {
      setDownloadError(errorMessage(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Deals by date — read only. Dates are when a deal was made."
        actions={
          <Button onClick={download} loading={downloading} disabled={!filter}>
            <Download className="size-4" aria-hidden /> Download CSV
          </Button>
        }
      />
      <Card className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="From">
          {(p) => <Input {...p} type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)} />}
        </Field>
        <Field label="To (included)" error={r ? null : "Must be on or after the start"}>
          {(p) => <Input {...p} type="date" value={toDay} onChange={(e) => setToDay(e.target.value)} />}
        </Field>
        <Field label="Asset">
          {(p) => (
            <Select {...p} value={asset} onChange={(e) => setAsset(e.target.value)}>
              <option value="">All assets</option>
              {symbols.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-fg">Status</span>
          <Segmented<Status>
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: "COMPLETED", label: "Completed" },
              { value: "ALL", label: "All" },
            ]}
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Segmented<Type>
            label="Type"
            value={type}
            onChange={setType}
            options={[
              { value: "ALL", label: "All" },
              { value: "DEPOSIT", label: "Deposits" },
              { value: "WITHDRAWAL", label: "Withdrawals" },
              { value: "SWAP", label: "Swaps" },
            ]}
          />
        </div>
      </Card>
      {downloadError && <Notice tone="danger">{downloadError}</Notice>}
      {report.error && <Notice tone="danger">{errorMessage(report.error)}</Notice>}

      {report.isPending && filter ? (
        <Skeleton className="h-48 rounded-card" />
      ) : report.data ? (
        <>
          <h2 className="mb-3 font-heading text-xl font-medium text-fg">Totals</h2>
          {report.data.totals.length === 0 ? (
            <Card>
              <EmptyState icon={<Spot name="history" />} title="No deals in this range" />
            </Card>
          ) : (
            <Card className="mb-6 overflow-x-auto p-0 sm:p-0">
              <table className="w-full min-w-[640px] text-sm" data-testid="deal-totals">
                <thead>
                  <tr className="border-b border-line text-left text-xs text-muted">
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Asset · network</th>
                    <th className="px-4 py-3 text-right font-medium">Count</th>
                    <th className="px-4 py-3 text-right font-medium">Amount</th>
                    <th className="px-4 py-3 text-right font-medium">Fees</th>
                    <th className="px-4 py-3 text-right font-medium">USD at the time</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.totals.map((t) => (
                    <tr
                      key={`${t.type}:${t.chain}:${t.asset}`}
                      className="border-b border-line last:border-0"
                    >
                      <td className="px-4 py-3 text-fg">{TYPE_LABEL[t.type]}</td>
                      <td className="px-4 py-3 text-muted">
                        {t.asset} · {t.chain}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{t.count}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {formatAmount(t.amount, 8)}
                        {t.received !== null && (
                          <span className="block text-xs text-muted">→ {formatAmount(t.received, 8)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {formatAmount(t.fee, 8)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {usd(t.usdCents)}
                        {t.withoutUsd > 0 && (
                          <span className="block text-xs text-muted">{t.withoutUsd} without a value</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {report.data.rows.length > 0 && (
            <>
              <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                <h2 className="font-heading text-xl font-medium text-fg">Deals</h2>
                {report.data.truncated && (
                  <Badge tone="warning">Showing the newest 500 — download the CSV for all</Badge>
                )}
              </div>
              <Card className="overflow-x-auto p-0 sm:p-0">
                <table className="w-full min-w-[760px] text-sm" data-testid="deal-rows">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-muted">
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Customer</th>
                      <th className="px-4 py-3 font-medium">Asset</th>
                      <th className="px-4 py-3 text-right font-medium">Amount</th>
                      <th className="px-4 py-3 text-right font-medium">USD</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data.rows.map((d) => (
                      <tr key={`${d.type}:${d.id}`} className="border-b border-line last:border-0">
                        <td className="px-4 py-3 whitespace-nowrap text-muted">{formatDateTime(d.at)}</td>
                        <td className="px-4 py-3 text-fg">{TYPE_LABEL[d.type].slice(0, -1)}</td>
                        <td className="max-w-56 truncate px-4 py-3 text-muted">{d.userEmail}</td>
                        <td className="px-4 py-3 text-muted">
                          {d.asset} · {d.chain}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums">
                          {formatAmount(d.amount, 8)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums">{usd(d.usdCents)}</td>
                        <td className="px-4 py-3 text-xs text-muted">{d.status.toLowerCase()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )}
        </>
      ) : null}
    </>
  );
}
